import React, { useCallback, useEffect } from 'react'
import { SelectBoxOption } from '../SelectBoxes'
import { noCase } from 'change-case'
import { StateSelect } from '../bulk-actions/StateSelect'
import { NumericalChange, resolveActionText } from '../bulk-actions/NumericalChange'
import { HTMLSelect } from '../ui/html-select'
import { ClassValue } from 'classnames/types'
import { ActionParams, hasExtraProperty } from '../../types/constants'
import { ActionsList } from '../../util/contstants'
import { stateOptions, UpdateReportAdTypesE } from '../../constants'
import { observer } from 'mobx-react-lite'
import { recipeStore } from '../../stores/recipe_store'
import { toJS } from 'mobx'
import classNames from 'classnames'
import cloneDeep from 'lodash.clonedeep'
export const ActionBadge = ({
    action,
    action_params,
}: {
    action: number
    action_params: ActionParams
}) => {
    let message
    // Use the discriminated union key for correct type narrowing.
    // `action` is kept for backwards-compat / historical payloads.
    const actionType = (action_params as any)?.type ?? action
    switch (actionType) {
        case ActionsList.SetState:
            message =
                'Change state to ' +
                noCase(stateOptions[(action_params as any).extra as number]?.text)
            break
        case ActionsList.SetDailyBudget:
            message = hasExtraProperty(action_params)
                ? resolveActionText(action_params.extra, 'daily budget')
                : ''
            break
        case ActionsList.SetDefaultBid:
            message = hasExtraProperty(action_params)
                ? resolveActionText(action_params.extra, 'default bid')
                : ''
            break
        case ActionsList.SetBid:
            message = hasExtraProperty(action_params)
                ? resolveActionText(action_params.extra, 'bid')
                : ''
            break
        case ActionsList.CreateExactMatchNegative:
            message = 'Create Exact Match Negative'
            break
        case ActionsList.ChangeBiddingStrategy:
            message = `Change bidding strategy to ${(action_params as any).extra ?? ''}`
            break
        case ActionsList.ChangeBidAdjustment:
            message = 'Change bid adjustment'
            break
        default:
            console.warn('Cannot resolve action for', action, 'with params', action_params)
    }
    return <div>{message}</div>
}

export const Actions = observer(({ className }: { className?: ClassValue }) => {
    const recipe = recipeStore.recipe

    const changed = useCallback(
        (action, extra) => {
            recipeStore.setAction(action, {
                type: action,
                extra,
            })
        },
        [recipeStore],
    )

    useEffect(() => {
        const extra = hasExtraProperty(recipe.action_params)
            ? recipe.action_params.extra
            : undefined
        changed(recipe.action, extra)
    }, [])

    console.log('recipe', toJS(recipe))

    return (
        <div>
            <div>
                <div
                    className={classNames('flex items-center', className)}
                    style={{ columnGap: '10px' }}
                >
                    <HTMLSelect
                        fill
                        iconProps={{ icon: 'caret-down' }}
                        value={recipe.action}
                        onChange={(e) => {
                            const type = parseInt(e.target.value)
                            recipeStore.setAction(type, { type })
                        }}
                        // Strip out content from the SelectBoxOption
                        options={recipeStore.availableActions.map((x) => ({
                            label: x.label,
                            value: x.value,
                        }))}
                    />
                    {recipe.action == ActionsList.SetState && <ActionStateChange />}
                    {recipe.action == ActionsList.SetDailyBudget && <ActionNumericalChange />}
                    {recipe.action == ActionsList.SetDefaultBid && <ActionNumericalChange />}
                    {recipe.action == ActionsList.SetBid && <ActionNumericalChange />}
                    {recipe.action == ActionsList.CreateExactMatchNegative && <div></div>}
                    {recipe.action == ActionsList.ChangeBiddingStrategy && <BiddingStrategyChange />}
                    {recipe.action == ActionsList.ChangeBidAdjustment && <BidAdjustmentChange />}
                </div>
            </div>
        </div>
    )
})

const BiddingStrategyChange = observer(() => {
    const value = (recipeStore.recipe.action_params as any).extra ?? 'SALES_DOWN_ONLY'
    return <HTMLSelect value={value} onChange={(e) => recipeStore.setAction(ActionsList.ChangeBiddingStrategy, { type: ActionsList.ChangeBiddingStrategy, extra: e.target.value } as ActionParams)} options={[
        { label: 'Dynamic bids – down only', value: 'SALES_DOWN_ONLY' },
        { label: 'Dynamic bids – up and down', value: 'SALES' },
        { label: 'Fixed bids', value: 'NONE' },
    ]} />
})

const BidAdjustmentChange = observer(() => {
    const extra = ((recipeStore.recipe.action_params as any).extra ?? {}) as Record<string, number>
    const set = (key: string, raw: string) => recipeStore.setAction(ActionsList.ChangeBidAdjustment, {
        type: ActionsList.ChangeBidAdjustment,
        extra: { ...extra, [key]: Math.max(0, Math.min(900, Number.parseInt(raw || '0', 10) || 0)) },
    } as ActionParams)
    return <div className="flex gap-2">{[['tos_placement', 'Top of search'], ['ros_placement', 'Rest of search'], ['pp_placement', 'Product pages']].map(([key, label]) =>
        <label key={key} className="text-xs">{label}<input type="number" min="0" max="900" value={extra[key] ?? 0} onChange={(e) => set(key, e.target.value)} /></label>)}</div>
})

interface ActionStateChangeProps {
    className?: ClassValue
}

const ActionStateChange = observer(({ className }: ActionStateChangeProps) => {
    const recipe = recipeStore.recipe

    const params = recipe.action_params
    // `ActionParams['type']` isn't a reliable discriminant here because `ActionsList.*` is typed as `number`.
    // Narrow using the presence/type of `extra` instead.
    if (!params || typeof (params as any).extra !== 'number') {
        return null
    }
    const stateValue = (params as any).extra as number

    return (
        <div className="pt-3">
            <div className="pb-0.5">State</div>
            <StateSelect
                className={className}
                value={stateValue}
                onChange={(type: number) => {
                    recipeStore.setAction(recipe.action, {
                        type: recipe.action,
                        extra: type,
                    })
                }}
            />
        </div>
    )
})

interface ActionNumericalChangeProps {
    className?: ClassValue
}

const ActionNumericalChange = observer(({ className }: ActionNumericalChangeProps) => {
    const recipe = recipeStore.recipe
    const version = recipeStore.version
    const variables = recipeStore.variables

    if (!hasExtraProperty(recipe.action_params)) {
        return null
    }

    const params = recipe.action_params.extra
    if (typeof params !== 'object') {
        return null
    }

    const variableOptions = Object.values(variables ?? {})
        .filter((v) => v.type === 'number')
        .map((v) => ({
            label: v.name,
            value: v.name,
        }))

    const setAction = (v: any) => {
        if (v.action !== params.action || v.value !== params.value || v.source !== params.source) {
            // If we are changin the source (ie value -> variable or vice, versa), then
            // we need to set a default value
            if (!v.source) {
                v.source = 'value'
            }

            if (v.source !== params.source) {
                if (v.source === 'value') {
                    v.value = 0
                } else if (Object.keys(variables).length > 0) {
                    v.value = Object.keys(variables)[0]
                } else {
                    v.value = ''
                }
            }

            recipeStore.setAction(recipe.action, {
                type: recipe.action,
                extra: cloneDeep(v),
            })
        }
    }

    return (
        <div className="pt-3 w-full">
            <div className="pb-0.5">Change Type</div>
            <NumericalChange
                className={className}
                value={params.value}
                selected={params.action}
                source={params.source ?? 'value'}
                onChange={setAction}
                version={version}
            />
            {params?.source === 'variable' && (
                <HTMLSelect
                    fill
                    iconProps={{ icon: 'caret-down' }}
                    className={classNames(className)}
                    value={params.value}
                    onChange={(e) => {
                        setAction({
                            action: params.action,
                            source: params.source,
                            value: e.target.value,
                        })
                    }}
                    options={variableOptions}
                />
            )}
        </div>
    )
})

export const SetStateAction = {
    label: 'Set State',
    value: ActionsList.SetState,
    content: () => <ActionStateChange />,
}

export const ChangeDailyBudgetAction = {
    label: 'Change Daily Budget',
    value: ActionsList.SetDailyBudget,
    content: () => <ActionNumericalChange />,
}
export const ChangeBiddingStrategyAction = {
    label: 'Change Bidding Strategy',
    value: ActionsList.ChangeBiddingStrategy,
}
export const ChangeBidAdjustmentAction = {
    label: 'Change Bid Adjustment',
    value: ActionsList.ChangeBidAdjustment,
}

export const ChangeDefaultBidAction = {
    label: 'Change Default Bid',
    value: ActionsList.SetDefaultBid,
    content: () => <ActionNumericalChange />,
}

export const ChangeBid = {
    label: 'Change Bid',
    value: ActionsList.SetBid,
    content: () => <ActionNumericalChange />,
}

export const CreateExactMatchNegativeAction = {
    label: 'Create Exact Match Negative',
    value: ActionsList.CreateExactMatchNegative,
    content: () => <div></div>,
}
